"use client";

import { useState, useEffect } from "react";
import { fetchGeoLocationData, transformGeoLocationData, type GeoLocationApiResponse } from "@/lib/api/geolocation";

interface GeoLocationData {
  countryCode: string;
  countryName: string;
  city: string;
  region: string;
  timezone: string;
  postalCode: string;
  latitude: string;
  longitude: string;
}

export function useGeoLocation() {
  const [geoData, setGeoData] = useState<GeoLocationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const detectLocation = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const apiData = await fetchGeoLocationData();
        const transformedData = transformGeoLocationData(apiData);

        if (transformedData) {
          setGeoData(transformedData);
        } else {
          setError(apiData.error || "Failed to detect location");
        }
      } catch (err) {
        console.error("Geolocation detection failed:", err);
        setError(err instanceof Error ? err.message : "Failed to detect location");
      } finally {
        setIsLoading(false);
      }
    };

    // Only run on client side and if we haven't detected yet
    if (typeof window !== "undefined" && !geoData && !error) {
      detectLocation();
    }
  }, [geoData, error]);

  return {
    geoData,
    isLoading,
    error,
  };
}
